/**
 * LLM Diagnostics Dashboard
 *
 * Admin interface for monitoring LLM operations, viewing errors,
 * and investigating issues without terminal access.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  Tab,
  Tabs,
  CircularProgress,
  Alert as MuiAlert,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Snackbar,
  Badge
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  Info as InfoIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  Storage as StorageIcon
} from '@mui/icons-material';
import { format, formatDistanceToNow } from 'date-fns';
import llmDiagnosticsApi from '../../services/llmDiagnosticsApi';
import { useSocket } from '../../context/SocketContext';

// Event type colors and labels
const EVENT_TYPE_CONFIG = {
  LLM_CALL_SUCCESS: { color: '#4caf50', label: 'Success', icon: CheckCircleIcon },
  LLM_CALL_ERROR: { color: '#f44336', label: 'Error', icon: ErrorIcon },
  LLM_FALLBACK_USED: { color: '#ff9800', label: 'Fallback', icon: WarningIcon },
  LLM_EMPTY_RESPONSE: { color: '#e91e63', label: 'Empty Response', icon: ErrorIcon },
  LLM_FUNCTION_CALL_FAILED: { color: '#9c27b0', label: 'Function Failed', icon: WarningIcon },
  LLM_RESPONSE_BLOCKED: { color: '#d32f2f', label: 'Blocked', icon: ErrorIcon },
  LLM_MESSAGE_EMITTED: { color: '#2196f3', label: 'Message Emitted', icon: InfoIcon }
};

const TIME_RANGES = [
  { value: 1, label: 'Last 1 hour' },
  { value: 6, label: 'Last 6 hours' },
  { value: 24, label: 'Last 24 hours' },
  { value: 72, label: 'Last 3 days' },
  { value: 168, label: 'Last 7 days' }
];

const LLMDiagnostics = ({ token }) => {
  // Socket connection
  const socket = useSocket();

  // State
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState(24);

  // Overview data
  const [overview, setOverview] = useState(null);

  // Logs data
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(0);
  const [logsPerPage, setLogsPerPage] = useState(25);
  const [logsFilters, setLogsFilters] = useState({
    eventType: '',
    errorsOnly: false,
    conversationId: ''
  });

  // Error breakdown
  const [errorBreakdown, setErrorBreakdown] = useState(null);

  // Trends
  const [trends, setTrends] = useState(null);

  // UI state
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);

  // Real-time alert state
  const [realtimeAlerts, setRealtimeAlerts] = useState([]);
  const [alertSnackbarOpen, setAlertSnackbarOpen] = useState(false);
  const [latestAlert, setLatestAlert] = useState(null);

  // Settings state
  const [loggingLevel, setLoggingLevel] = useState('all');
  const [storageEstimate, setStorageEstimate] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsExpanded, setSettingsExpanded] = useState(false);

  // Socket.io listener for real-time LLM error alerts
  useEffect(() => {
    if (!socket) return;

    const handleLLMAlert = (notification) => {
      console.log('[LLMDiagnostics] Received real-time alert:', notification);
      setRealtimeAlerts(prev => [notification, ...prev].slice(0, 10)); // Keep last 10
      setLatestAlert(notification);
      setAlertSnackbarOpen(true);
    };

    socket.on('llm_error_alert', handleLLMAlert);

    return () => {
      socket.off('llm_error_alert', handleLLMAlert);
    };
  }, [socket]);

  // Load data based on active tab
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      switch (activeTab) {
        case 0: // Overview
          const overviewData = await llmDiagnosticsApi.getOverview(timeRange);
          setOverview(overviewData);
          break;
        case 1: // Log Explorer
          const logsData = await llmDiagnosticsApi.getLogs({
            ...logsFilters,
            limit: logsPerPage,
            offset: logsPage * logsPerPage
          });
          setLogs(logsData.logs);
          setLogsTotal(logsData.total);
          break;
        case 2: // Error Analysis
          const breakdownData = await llmDiagnosticsApi.getErrorBreakdown(timeRange);
          setErrorBreakdown(breakdownData);
          break;
        case 3: // Trends
          const trendsData = await llmDiagnosticsApi.getTrends(timeRange, timeRange > 48 ? 'day' : 'hour');
          setTrends(trendsData);
          break;
        default:
          break;
      }
    } catch (err) {
      console.error('[LLMDiagnostics] Error loading data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [activeTab, timeRange, logsFilters, logsPage, logsPerPage]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load settings and storage estimate on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const [settings, storage] = await Promise.all([
          llmDiagnosticsApi.getSettings(),
          llmDiagnosticsApi.getStorageEstimate()
        ]);
        setLoggingLevel(settings.loggingLevel || 'all');
        setStorageEstimate(storage);
      } catch (err) {
        console.error('[LLMDiagnostics] Error loading settings:', err);
      }
    };
    loadSettings();
  }, []);

  // Handle logging level change
  const handleLoggingLevelChange = async (newLevel) => {
    setSettingsLoading(true);
    try {
      await llmDiagnosticsApi.updateLoggingLevel(newLevel);
      setLoggingLevel(newLevel);
    } catch (err) {
      console.error('[LLMDiagnostics] Error updating logging level:', err);
      setError('Failed to update logging level');
    } finally {
      setSettingsLoading(false);
    }
  };

  // Handle export
  const handleExport = async () => {
    try {
      await llmDiagnosticsApi.exportLogs({
        ...logsFilters,
        limit: 5000
      });
    } catch (err) {
      console.error('[LLMDiagnostics] Export error:', err);
      setError('Failed to export logs');
    }
  };

  // Render overview tab
  const renderOverview = () => {
    if (!overview) return null;

    const { summary, latency, recentErrors } = overview;

    return (
      <Box>
        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Success Rate</Typography>
                <Typography variant="h4" color={summary.successRate >= 95 ? 'success.main' : summary.successRate >= 80 ? 'warning.main' : 'error.main'}>
                  {summary.successRate}%
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {summary.successCount} / {summary.totalCalls} calls
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Errors</Typography>
                <Typography variant="h4" color={summary.errorCount > 0 ? 'error.main' : 'success.main'}>
                  {summary.errorCount}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  + {summary.emptyResponseCount} empty responses
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Fallbacks</Typography>
                <Typography variant="h4" color={summary.fallbackCount > 0 ? 'warning.main' : 'success.main'}>
                  {summary.fallbackCount}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Secondary adapter used
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Avg Latency</Typography>
                <Typography variant="h4">
                  {latency.avg}ms
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  min: {latency.min}ms / max: {latency.max}ms
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Recent Errors */}
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Recent Errors</Typography>
          {recentErrors.length === 0 ? (
            <MuiAlert severity="success">No errors in the selected time range</MuiAlert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    <TableCell>Event Type</TableCell>
                    <TableCell>Adapter</TableCell>
                    <TableCell>Model</TableCell>
                    <TableCell>Persona</TableCell>
                    <TableCell>Error</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentErrors.map((log) => (
                    <TableRow key={log._id} hover>
                      <TableCell>
                        <Tooltip title={format(new Date(log.timestamp), 'PPpp')}>
                          <span>{formatDistanceToNow(new Date(log.timestamp), { addSuffix: true })}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={EVENT_TYPE_CONFIG[log.eventType]?.label || log.eventType}
                          size="small"
                          sx={{ bgcolor: EVENT_TYPE_CONFIG[log.eventType]?.color, color: 'white' }}
                        />
                      </TableCell>
                      <TableCell>{log.details?.adapter || '-'}</TableCell>
                      <TableCell>{log.details?.model || '-'}</TableCell>
                      <TableCell>{log.details?.personaKey || '-'}</TableCell>
                      <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {log.details?.errorMessage || log.details?.errorCode || '-'}
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => { setSelectedLog(log); setDetailDialogOpen(true); }}>
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>
    );
  };

  // Render log explorer tab
  const renderLogExplorer = () => {
    return (
      <Box>
        {/* Filters */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Event Type</InputLabel>
                <Select
                  value={logsFilters.eventType}
                  label="Event Type"
                  onChange={(e) => setLogsFilters({ ...logsFilters, eventType: e.target.value })}
                >
                  <MenuItem value="">All Types</MenuItem>
                  {Object.entries(EVENT_TYPE_CONFIG).map(([type, config]) => (
                    <MenuItem key={type} value={type}>{config.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Conversation ID"
                value={logsFilters.conversationId}
                onChange={(e) => setLogsFilters({ ...logsFilters, conversationId: e.target.value })}
                placeholder="Enter conversation ID..."
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button
                variant={logsFilters.errorsOnly ? 'contained' : 'outlined'}
                color="error"
                onClick={() => setLogsFilters({ ...logsFilters, errorsOnly: !logsFilters.errorsOnly })}
                startIcon={<ErrorIcon />}
                fullWidth
              >
                Errors Only
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button
                variant="outlined"
                onClick={loadData}
                startIcon={<SearchIcon />}
                fullWidth
              >
                Search
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button
                variant="outlined"
                onClick={handleExport}
                startIcon={<DownloadIcon />}
                fullWidth
              >
                Export CSV
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Logs Table */}
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Event Type</TableCell>
                  <TableCell>Conversation</TableCell>
                  <TableCell>Adapter</TableCell>
                  <TableCell>Model</TableCell>
                  <TableCell>Persona</TableCell>
                  <TableCell>Details</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => (
                  <React.Fragment key={log._id}>
                    <TableRow hover onClick={() => setExpandedRow(expandedRow === log._id ? null : log._id)}>
                      <TableCell>
                        <Tooltip title={format(new Date(log.timestamp), 'PPpp')}>
                          <span>{formatDistanceToNow(new Date(log.timestamp), { addSuffix: true })}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={EVENT_TYPE_CONFIG[log.eventType]?.label || log.eventType}
                          size="small"
                          sx={{ bgcolor: EVENT_TYPE_CONFIG[log.eventType]?.color, color: 'white' }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {log.conversationId ? log.conversationId.toString().slice(-8) : '-'}
                      </TableCell>
                      <TableCell>{log.details?.adapter || '-'}</TableCell>
                      <TableCell>{log.details?.model || '-'}</TableCell>
                      <TableCell>{log.details?.personaKey || '-'}</TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {log.details?.errorMessage || log.details?.latencyMs ? `${log.details.latencyMs}ms` : '-'}
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setSelectedLog(log); setDetailDialogOpen(true); }}>
                          <InfoIcon fontSize="small" />
                        </IconButton>
                        {expandedRow === log._id ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={8} sx={{ py: 0 }}>
                        <Collapse in={expandedRow === log._id}>
                          <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                            <Typography variant="subtitle2" gutterBottom>Full Details:</Typography>
                            <pre style={{ margin: 0, fontSize: '0.75rem', overflow: 'auto', maxHeight: 200 }}>
                              {JSON.stringify(log.details, null, 2)}
                            </pre>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={logsTotal}
            page={logsPage}
            rowsPerPage={logsPerPage}
            onPageChange={(e, page) => setLogsPage(page)}
            onRowsPerPageChange={(e) => { setLogsPerPage(parseInt(e.target.value)); setLogsPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Paper>
      </Box>
    );
  };

  // Render error analysis tab
  const renderErrorAnalysis = () => {
    if (!errorBreakdown) return null;

    const { byErrorCode, byAdapter, byPersona } = errorBreakdown;

    return (
      <Box>
        <Grid container spacing={3}>
          {/* By Error Code */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>By Error Code</Typography>
              {byErrorCode.length === 0 ? (
                <MuiAlert severity="success">No errors in the selected time range</MuiAlert>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Error Code</TableCell>
                        <TableCell align="right">Count</TableCell>
                        <TableCell>Adapters</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {byErrorCode.map((item) => (
                        <TableRow key={item._id || 'unknown'}>
                          <TableCell>
                            <Chip label={item._id || 'UNKNOWN'} size="small" color="error" variant="outlined" />
                          </TableCell>
                          <TableCell align="right">{item.count}</TableCell>
                          <TableCell>{item.adapters?.join(', ') || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>

          {/* By Adapter */}
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>By Adapter</Typography>
              {byAdapter.length === 0 ? (
                <Typography color="textSecondary">No data</Typography>
              ) : (
                <Box>
                  {byAdapter.map((item) => (
                    <Box key={item._id || 'unknown'} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid #eee' }}>
                      <Typography>{item._id || 'Unknown'}</Typography>
                      <Chip label={item.count} size="small" color="error" />
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>
          </Grid>

          {/* By Persona */}
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>By Persona</Typography>
              {byPersona.length === 0 ? (
                <Typography color="textSecondary">No data</Typography>
              ) : (
                <Box>
                  {byPersona.map((item) => (
                    <Box key={item._id || 'unknown'} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid #eee' }}>
                      <Typography>{item._id || 'Unknown'}</Typography>
                      <Chip label={item.count} size="small" color="warning" />
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  };

  // Render trends tab (simplified - could be enhanced with charting library)
  const renderTrends = () => {
    if (!trends) return null;

    return (
      <Box>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>LLM Operation Trends</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Period</TableCell>
                  <TableCell align="right">Success</TableCell>
                  <TableCell align="right">Error</TableCell>
                  <TableCell align="right">Fallback</TableCell>
                  <TableCell align="right">Empty Response</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trends.trends.map((t) => (
                  <TableRow key={t.period}>
                    <TableCell>{t.period}</TableCell>
                    <TableCell align="right">
                      <Chip label={t.success} size="small" color="success" variant="outlined" />
                    </TableCell>
                    <TableCell align="right">
                      {t.error > 0 && <Chip label={t.error} size="small" color="error" />}
                    </TableCell>
                    <TableCell align="right">
                      {t.fallback > 0 && <Chip label={t.fallback} size="small" color="warning" />}
                    </TableCell>
                    <TableCell align="right">
                      {t.emptyResponse > 0 && <Chip label={t.emptyResponse} size="small" color="secondary" />}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">LLM Diagnostics</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Time Range</InputLabel>
            <Select
              value={timeRange}
              label="Time Range"
              onChange={(e) => setTimeRange(e.target.value)}
            >
              {TIME_RANGES.map(r => (
                <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton onClick={loadData} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Settings Panel */}
      <Paper sx={{ mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            cursor: 'pointer',
            '&:hover': { bgcolor: 'grey.50' }
          }}
          onClick={() => setSettingsExpanded(!settingsExpanded)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SettingsIcon color="action" />
            <Typography variant="subtitle1">Logging Settings</Typography>
            <Chip
              label={loggingLevel === 'all' ? 'All Events' : loggingLevel === 'errors_only' ? 'Errors Only' : 'Off'}
              size="small"
              color={loggingLevel === 'all' ? 'success' : loggingLevel === 'errors_only' ? 'warning' : 'default'}
            />
          </Box>
          {settingsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>
        <Collapse in={settingsExpanded}>
          <Box sx={{ p: 2, pt: 0, borderTop: '1px solid #eee' }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Logging Level</InputLabel>
                  <Select
                    value={loggingLevel}
                    label="Logging Level"
                    onChange={(e) => handleLoggingLevelChange(e.target.value)}
                    disabled={settingsLoading}
                  >
                    <MenuItem value="all">All (Log Everything)</MenuItem>
                    <MenuItem value="errors_only">Errors Only</MenuItem>
                    <MenuItem value="off">Off (No Logging)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                {storageEstimate && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <StorageIcon color="action" />
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        LLM Event Storage
                      </Typography>
                      <Typography variant="h6">
                        {storageEstimate.llmEvents?.estimatedSizeMB || '0'} MB
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {(storageEstimate.llmEvents?.totalDocuments || 0).toLocaleString()} log entries
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Grid>
              <Grid item xs={12} md={4}>
                <MuiAlert severity="info" sx={{ py: 0.5 }}>
                  <Typography variant="caption">
                    {loggingLevel === 'off' && 'Logging is disabled. No LLM events are being recorded.'}
                    {loggingLevel === 'errors_only' && 'Only errors and failures are being logged. Success events are not recorded.'}
                    {loggingLevel === 'all' && 'All LLM calls are being logged for full diagnostics.'}
                  </Typography>
                </MuiAlert>
              </Grid>
            </Grid>
            {storageEstimate?.archival?.enabled && (
              <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="caption" color="textSecondary">
                  BigQuery archival is enabled. Events older than {storageEstimate.retentionDays} days are archived automatically.
                  {storageEstimate.archival.lastRun && (
                    <> Last archival: {formatDistanceToNow(new Date(storageEstimate.archival.lastRun), { addSuffix: true })}</>
                  )}
                </Typography>
              </Box>
            )}
          </Box>
        </Collapse>
      </Paper>

      {/* Error display */}
      {error && (
        <MuiAlert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </MuiAlert>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 3 }}>
        <Tab label="Overview" />
        <Tab label="Log Explorer" />
        <Tab label="Error Analysis" />
        <Tab label="Trends" />
      </Tabs>

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Tab content */}
      {!loading && (
        <>
          {activeTab === 0 && renderOverview()}
          {activeTab === 1 && renderLogExplorer()}
          {activeTab === 2 && renderErrorAnalysis()}
          {activeTab === 3 && renderTrends()}
        </>
      )}

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Log Entry Details</DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Event Type</Typography>
                  <Chip
                    label={EVENT_TYPE_CONFIG[selectedLog.eventType]?.label || selectedLog.eventType}
                    sx={{ bgcolor: EVENT_TYPE_CONFIG[selectedLog.eventType]?.color, color: 'white' }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Timestamp</Typography>
                  <Typography>{format(new Date(selectedLog.timestamp), 'PPpp')}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Conversation ID</Typography>
                  <Typography sx={{ fontFamily: 'monospace' }}>{selectedLog.conversationId || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">User ID</Typography>
                  <Typography sx={{ fontFamily: 'monospace' }}>{selectedLog.userId || '-'}</Typography>
                </Grid>
              </Grid>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>Details</Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                <pre style={{ margin: 0, fontSize: '0.85rem', overflow: 'auto', maxHeight: 400 }}>
                  {JSON.stringify(selectedLog.details, null, 2)}
                </pre>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Real-time Alert Snackbar */}
      <Snackbar
        open={alertSnackbarOpen}
        autoHideDuration={6000}
        onClose={() => setAlertSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MuiAlert
          severity={latestAlert?.priority === 'urgent' ? 'error' : 'warning'}
          onClose={() => setAlertSnackbarOpen(false)}
          sx={{ width: '100%' }}
        >
          <Typography variant="subtitle2">{latestAlert?.title}</Typography>
          <Typography variant="body2">{latestAlert?.message}</Typography>
        </MuiAlert>
      </Snackbar>

      {/* Real-time Alerts Panel */}
      {realtimeAlerts.length > 0 && (
        <Paper
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            width: 320,
            maxHeight: 300,
            overflow: 'auto',
            zIndex: 1000,
            p: 2
          }}
          elevation={4}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2">
              <Badge badgeContent={realtimeAlerts.length} color="error" sx={{ mr: 1 }}>
                <NotificationsIcon fontSize="small" />
              </Badge>
              Real-time Alerts
            </Typography>
            <Button size="small" onClick={() => setRealtimeAlerts([])}>Clear</Button>
          </Box>
          {realtimeAlerts.map((alert, idx) => (
            <MuiAlert
              key={idx}
              severity={alert.priority === 'urgent' ? 'error' : 'warning'}
              sx={{ mb: 1 }}
            >
              <Typography variant="body2" fontWeight="bold">{alert.title}</Typography>
              <Typography variant="caption">{alert.message}</Typography>
            </MuiAlert>
          ))}
        </Paper>
      )}
    </Box>
  );
};

export default LLMDiagnostics;
